import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Button, Flex, Image, Spinner, Text, useToast,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { takeLinkedId } from '../../hooks/useUrlSyncedId';
import { useTranslation } from 'react-i18next';
import { LuFileUp } from 'react-icons/lu';
import {
  fetchAccountFreshness, getProcessStatus, listProcesses, retryProcess,
} from '../../api/endpointsBackend';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useFilters } from '../../contexts/FilterContext';
import { useStatementRepairFlags } from '../../hooks/useStatementRepair';
import { DATE_MODES } from '../../utils/dateUtils';
import { formatDayShort, formatDateShort } from '../../utils/intlFormatters';
import SourceFilesUploader from '../source-files/SourceFilesUploader';
import { isActiveStatus, STATUS_CONFIG } from '../source-files/sourceFilesUI';
import StatementReviewCard from '../repairs/checkup/StatementReviewCard';
import CheckupBanner from '../repairs/checkup/CheckupBanner';
import StatementFlagChips from '../repairs/StatementFlagChips';
import StatusGlyph from '../shared/StatusGlyph';
import PhoneSheet, { usePhoneSheet } from './PhoneSheet';
import DesktopOnlyNote from './DesktopOnlyNote';
import { DesktopNoteCard } from './PhoneReadOnlyParts';

// The same cadence the desktop page polls running imports at.
const POLL_INTERVAL_MS = 2500;
// Recent shows the latest uploads; the full history stays on the computer.
const RECENT_COUNT = 20;
// "Needs attention" looks through as many uploads as the desktop list loads.
const ATTENTION_COUNT = 100;

// The pills in the phone's colours, which keep 4.5:1 in both modes; a
// cancelled upload keeps the desktop's grey.
const PILL_TONE = {
  completed: { color: 'phone.income', bg: 'rgba(21, 128, 61, 0.10)', _dark: { bg: 'rgba(74, 222, 128, 0.14)' } },
  failed: { color: 'phone.expense', bg: 'rgba(185, 28, 28, 0.08)', _dark: { bg: 'rgba(248, 113, 113, 0.14)' } },
  running: { color: 'phone.newPillFg', bg: 'phone.newPillBg' },
  pending: { color: 'phone.newPillFg', bg: 'phone.newPillBg' },
};

// A hairline between rows, from where the text starts.
const divider = (left) => ({
  content: '""', position: 'absolute', top: 0, left, right: 0, h: '1px', bg: 'redesign.border',
});

// A statement status's chip: its glyph and word on a tint of its colour, as
// the computer's Dashboard draws them.
const FRESHNESS_PILL = {
  up_to_date: { bg: 'rgba(21, 128, 61, 0.10)', color: 'phone.income', _dark: { bg: 'rgba(74, 222, 128, 0.14)' } },
  behind: { bg: 'rgba(180, 83, 9, 0.12)', color: 'redesign.text.warn', _dark: { bg: 'rgba(250, 204, 21, 0.14)' } },
  outdated: { bg: 'rgba(185, 28, 28, 0.08)', color: 'phone.expense', _dark: { bg: 'rgba(248, 113, 113, 0.14)' } },
  none: { bg: 'redesign.bg.card', color: 'phone.muted' },
};

const uploadDateOf = (p) => p.source_file?.upload_date || p.started_at || null;

// A naive backend stamp is UTC; mark it so before parsing.
const parseStamp = (s) => {
  if (!s) return null;
  const withZone = /Z|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`;
  const d = new Date(withZone);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** An upload's age for the list: "2 min ago" within a day, the date before that. */
function uploadStamp(iso, t) {
  const d = parseStamp(iso);
  if (!d) return '';
  const minutes = Math.floor((Date.now() - d.getTime()) / 60000);
  if (minutes < 1) return t('dates:relative.justNow');
  if (minutes < 60) return t('phone.minutesAgo', { count: minutes });
  if (minutes < 24 * 60) return t('phone.hoursAgo', { count: Math.floor(minutes / 60) });
  return formatDateShort(d.toISOString());
}

const movementsCreated = (p) => p.steps?.find((s) => s.step_number === 5)?.results?.movement?.created?.count ?? null;

function SectionTitle({ children, pt = '16px' }) {
  return (
    <Text textStyle="phoneCaption" color="phone.muted" px="16px" pt={pt} pb="8px">{children}</Text>
  );
}

function Card({ children }) {
  return (
    <Box
      mx="16px"
      borderRadius="14px"
      bg="redesign.bg.surface"
      border="1px solid"
      borderColor="redesign.border"
      overflow="hidden"
    >
      {children}
    </Box>
  );
}

function Disc({ children, size = '36px', bg = 'redesign.bg.card' }) {
  return (
    <Flex
      w={size}
      h={size}
      borderRadius="full"
      bg={bg}
      align="center"
      justify="center"
      flexShrink={0}
      fontSize="18px"
      overflow="hidden"
    >
      {children}
    </Flex>
  );
}

function StatusPill({ status }) {
  const { t } = useTranslation('sourceFiles');
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.cancelled;
  const tone = PILL_TONE[status] || cfg;
  return (
    <Flex
      as="span"
      align="center"
      gap="4px"
      px="8px"
      h="22px"
      borderRadius="full"
      bg={tone.bg}
      _dark={tone._dark}
      color={tone.color}
      fontSize="13px"
      fontWeight="600"
      flexShrink={0}
    >
      {isActiveStatus(status) && <Spinner size="xs" speed="0.8s" />}
      {t(`status.${cfg.labelKey}`)}
    </Flex>
  );
}

function FreshnessRow({ account, first }) {
  const { t } = useTranslation(['sourceFiles', 'dashboard']);
  const label = t(`dashboard:statementStatus.status.${account.status}`, { defaultValue: account.status });
  const through = account.covered_through
    ? t('phone.through', { date: formatDayShort(account.covered_through) })
    : null;
  return (
    <Flex
      position="relative"
      align="center"
      gap="12px"
      px="14px"
      py="7px"
      minH="54px"
      _before={first ? undefined : divider('54px')}
    >
      <Disc size="28px">
        {account.source_logo_url ? (
          <Image
            src={account.source_logo_url}
            alt=""
            boxSize="18px"
            objectFit="contain"
            fallback={<Text as="span" fontSize="14px">{account.icon || '🏦'}</Text>}
          />
        ) : <Text as="span" fontSize="14px">{account.icon || '🏦'}</Text>}
      </Disc>
      <Box flex="1" minW={0}>
        {/* Wraps rather than truncates: two accounts at one bank differ at the end. */}
        <Text textStyle="phoneRowName" fontWeight="400" color="redesign.heading">{account.account_name}</Text>
        <Flex align="center" wrap="wrap" columnGap="8px" rowGap="2px" mt="2px">
          <Flex
            as="span"
            align="center"
            gap="5px"
            h="22px"
            pl="5px"
            pr="8px"
            borderRadius="6px"
            {...(FRESHNESS_PILL[account.status] || FRESHNESS_PILL.none)}
          >
            <StatusGlyph status={account.status} />
            <Text as="span" textStyle="phoneMeta" fontWeight="600" color="inherit">{label}</Text>
          </Flex>
          {through && <Text as="span" textStyle="phoneMeta" color="phone.muted">{through}</Text>}
        </Flex>
      </Box>
    </Flex>
  );
}

/**
 * A processed upload's outcome, read-only, with the way to its movements
 * and, for the Owner and Co-Owners, "Check this statement".
 */
function OutcomeSheet({ process, isOpen, onClose, repairFlags }) {
  const { t } = useTranslation('sourceFiles');
  const created = process ? movementsCreated(process) : null;
  const sourceFileId = process?.source_file?.id;
  return (
    <PhoneSheet
      id="statement-outcome"
      isOpen={isOpen}
      onClose={onClose}
      title={process?.source_file?.filename || t('list.unknownFile')}
    >
      <Box px="16px" pt="8px">
        {/* Without step 5's results there is no count to claim. */}
        {created != null && (
          <Text textStyle="phoneBody" color="redesign.heading">
            {t(created === 1 ? 'phone.createdOne' : 'phone.createdOther', { count: created })}
          </Text>
        )}
        {process?.source_file?.id && <OpenInMovements sourceFileId={process.source_file.id} />}
        {sourceFileId && repairFlags && (
          <Box mt="24px">
            <StatementReviewCard sourceFileId={sourceFileId} flags={repairFlags} phone />
          </Box>
        )}
      </Box>
    </PhoneSheet>
  );
}

function OpenInMovements({ sourceFileId }) {
  const { t } = useTranslation('sourceFiles');
  const navigate = useNavigate();
  const { close } = usePhoneSheet();
  const { clearFilters, setDateMode, addFilter } = useFilters();
  const open = async () => {
    // The sheet's own back step must land before the filters are written.
    await close();
    clearFilters();
    // A statement for an old period still shows.
    setDateMode(DATE_MODES.ALL);
    addFilter({ entity: 'SourceFile', column: 'id', operator: 'in', values: [sourceFileId] });
    navigate('/movements');
  };
  return (
    <Button variant="phonePrimary" w="100%" mt="16px" onClick={open}>
      {t('phone.openInMovements')}
    </Button>
  );
}

// The "Needs attention" filter, drawn as the Movements page draws its chips:
// a 36px pill in a 44px tap target, outlined in the alert colour until on.
function AttentionChip({ active, count, onToggle }) {
  const { t } = useTranslation('repairs');
  return (
    <Box
      as="button"
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      display="flex"
      alignItems="center"
      h="44px"
      mx="16px"
      sx={{ WebkitTapHighlightColor: 'transparent' }}
    >
      <Flex
        align="center"
        h="36px"
        px="13px"
        borderRadius="18px"
        border="1px solid"
        borderColor={active ? 'phone.link' : 'phone.alert'}
        bg={active ? 'redesign.bg.accent' : 'redesign.bg.card'}
        color={active ? 'redesign.heading' : 'redesign.text'}
        fontSize="14px"
        fontWeight="500"
        whiteSpace="nowrap"
      >
        {t('filter.needsAttention', { count })}
      </Flex>
    </Box>
  );
}

function RecentRow({ process, first, canRetry, onOpen, onRetry, repairFlags }) {
  const { t } = useTranslation(['sourceFiles', 'dates']);
  const sf = process.source_file;
  const created = process.status === 'completed' ? movementsCreated(process) : null;
  const tappable = process.status === 'completed';

  const content = (
    <>
      <Disc size="40px" bg="redesign.border"><Box as={LuFileUp} boxSize="20px" color="phone.muted" /></Disc>
      <Box flex="1" minW={0}>
        <Text textStyle="phoneRowName" color="redesign.heading" wordBreak="break-word">
          {sf?.filename || t('list.unknownFile')}
        </Text>
        <Flex align="center" wrap="wrap" columnGap="8px" rowGap="2px" mt="2px">
          <Text as="span" textStyle="phoneMeta" color="phone.muted">{uploadStamp(uploadDateOf(process), t)}</Text>
          <StatusPill status={process.status} />
          {created != null && (
            <Text as="span" textStyle="phoneMeta" color="phone.muted">
              {t(created === 1 ? 'phone.movementsOne' : 'phone.movementsOther', { count: created })}
            </Text>
          )}
          <StatementFlagChips flags={repairFlags} phone />
        </Flex>
      </Box>
    </>
  );

  return (
    <Flex
      as={tappable ? 'button' : 'div'}
      type={tappable ? 'button' : undefined}
      onClick={tappable ? () => onOpen(process) : undefined}
      position="relative"
      _before={first ? undefined : divider('68px')}
      align="center"
      gap="12px"
      w="100%"
      minH="64px"
      px="16px"
      py="10px"
      textAlign="left"
      _active={tappable ? { bg: 'redesign.bg.hover' } : undefined}
    >
      {content}
      {process.status === 'failed' && canRetry && (
        <Button
          variant="outline"
          h="44px"
          px="16px"
          borderRadius="12px"
          fontSize="15px"
          fontWeight="600"
          flexShrink={0}
          onClick={() => onRetry(process.process_id)}
        >
          {t('phone.retry')}
        </Button>
      )}
    </Flex>
  );
}

/**
 * Statements on the phone: upload, which accounts'
 * statements are behind, and the latest uploads with their status. For the
 * Owner and Co-Owners the uploads carry their repair flags, "Needs attention"
 * narrows the list to the flagged ones, and an upload's sheet checks its
 * statement. Pipeline steps, search, the account filter and delete stay on
 * the computer. A role that cannot read statements (a deep link from a
 * Viewer) gets the desktop note and no requests.
 */
export default function PhoneStatements() {
  const { can } = useWorkspace();
  return can('statement.read') ? <StatementsBody /> : <DesktopOnlyNote withHomeLink />;
}

function StatementsBody() {
  const { t } = useTranslation(['sourceFiles', 'repairs']);
  const toast = useToast();
  const { can } = useWorkspace();
  const canReadFiles = can('source_file.read');
  // The repair's flags: null (no chips, no filter) unless the repair answers.
  const { flags: repairFlags, attentionCount } =
    useStatementRepairFlags(can('repair.review'));
  const [attentionOnly, setAttentionOnly] = useState(false);
  const showAttention = attentionOnly && Boolean(repairFlags);
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [freshness, setFreshness] = useState(null);
  // The last opened upload stays while its sheet slides away.
  const [outcome, setOutcome] = useState({ process: null, isOpen: false });
  const pollRef = useRef(null);

  // `?source_file_id=<id>` (a statement in the chat) opens that file's upload
  // once the list is in, when it is among the recent ones.
  useEffect(() => {
    if (loading) return;
    const linked = takeLinkedId('source_file_id');
    const upload = linked ? processes.find((p) => Number(p.source_file?.id) === linked) : null;
    if (upload) setOutcome({ process: upload, isOpen: true });
  }, [loading, processes]);

  const loadProcesses = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const result = await listProcesses({ pageSize: showAttention ? ATTENTION_COUNT : RECENT_COUNT });
      const list = [...(result.processes || [])].sort(
        (a, b) => new Date(uploadDateOf(b) || 0) - new Date(uploadDateOf(a) || 0),
      );
      setProcesses(list);
    } catch (err) {
      toast({ title: t('toasts.errorTitle'), description: t('toasts.loadFailed'), status: 'error', duration: 5000, isClosable: true });
    } finally {
      if (!silent) setLoading(false);
    }
  }, [toast, t, showAttention]);

  const loadFreshness = useCallback(() => {
    if (!canReadFiles) return;
    fetchAccountFreshness()
      .then(setFreshness)
      .catch(() => setFreshness({ accounts: [] }));
  }, [canReadFiles]);

  const refresh = useCallback(() => {
    loadProcesses({ silent: true });
    loadFreshness();
  }, [loadProcesses, loadFreshness]);

  useEffect(() => {
    loadProcesses();
    loadFreshness();
  }, [loadProcesses, loadFreshness]);

  // Running imports are polled one by one, as on the desktop, so a pill
  // updates live; once one finishes, the list and the freshness reload.
  useEffect(() => {
    const active = processes.filter((p) => isActiveStatus(p.status));
    if (active.length === 0) return undefined;
    const tick = async () => {
      const updates = await Promise.allSettled(active.map((p) => getProcessStatus(p.process_id)));
      setProcesses((prev) => prev.map((p) => {
        const idx = active.findIndex((a) => a.process_id === p.process_id);
        if (idx === -1 || updates[idx].status !== 'fulfilled') return p;
        return { ...p, ...updates[idx].value };
      }));
      if (updates.some((r) => r.status === 'fulfilled' && !isActiveStatus(r.value.status))) refresh();
    };
    pollRef.current = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [processes, refresh]);

  const handleRetry = useCallback(async (processId) => {
    try {
      await retryProcess(processId);
      toast({ title: t('toasts.retryingTitle'), status: 'info', duration: 3000, isClosable: true });
      loadProcesses({ silent: true });
    } catch (error) {
      toast({
        title: t('toasts.retryFailedTitle'),
        description: error.response?.data?.detail || t('toasts.retryFailedDescription'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [toast, t, loadProcesses]);

  const freshnessAccounts = freshness?.accounts || [];
  const shown = showAttention
    ? processes.filter((p) => repairFlags[p.source_file?.id]?.needs_attention)
    : processes;

  return (
    <Box pb="8px">
      <Box pt="8px"><CheckupBanner flags={repairFlags} phone /></Box>
      {can('statement.upload') && (
        <Box px="16px" pt="8px">
          <SourceFilesUploader onUploadStarted={refresh} />
        </Box>
      )}

      {freshnessAccounts.length > 0 && (
        <>
          <SectionTitle pt="12px">{t('phone.statusByAccount')}</SectionTitle>
          <Card>
            {freshnessAccounts.map((a, i) => <FreshnessRow key={a.account_id} account={a} first={i === 0} />)}
          </Card>
        </>
      )}

      <SectionTitle>{t('phone.recent')}</SectionTitle>
      {repairFlags && (
        <AttentionChip active={attentionOnly} count={attentionCount} onToggle={() => setAttentionOnly((on) => !on)} />
      )}
      {loading ? (
        <Flex justify="center" py="24px"><Spinner size="md" color="phone.muted" /></Flex>
      ) : shown.length === 0 ? (
        <Text textStyle="phoneBody" color="phone.muted" px="16px" py="8px">
          {showAttention ? t('repairs:filter.empty') : t('phone.noFiles')}
        </Text>
      ) : (
        shown.map((p, i) => (
          <RecentRow
            key={p.process_id}
            process={p}
            first={i === 0}
            canRetry={can('statement.upload')}
            onOpen={(process) => setOutcome({ process, isOpen: true })}
            onRetry={handleRetry}
            repairFlags={repairFlags?.[p.source_file?.id]}
          />
        ))
      )}

      <DesktopNoteCard />
      <OutcomeSheet
        process={outcome.process}
        isOpen={outcome.isOpen}
        onClose={() => setOutcome((o) => ({ ...o, isOpen: false }))}
        repairFlags={repairFlags?.[outcome.process?.source_file?.id]}
      />
    </Box>
  );
}
